import React, { useState } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import config from "../../../util/config";

interface UniversityFormData {
  institutionName: string;
  website: string;
  contactPerson: string;
  designation: string;
  email: string;
  mobileNumber: string;
  alternateMobileNumber?: string;
  participationCategory: string;
}

const UniversitiesRegistrationForm: React.FC = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UniversityFormData>();

  const [loading, setLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const onSubmit = async (data: UniversityFormData) => {
    const saveOrUpdateUrl = "http://localhost:8081/api/university/saveOrUpdate";
    const leadUrl = "http://localhost:8081/api/leads/university/create";

    const universityPayload = {
      institutionName: data.institutionName,
      website: data.website,
      pointOfContact: data.contactPerson,
      designation: data.designation,
      email: data.email,
      mobileNumber: data.mobileNumber,
      alternateMobileNumber: data.alternateMobileNumber || "",
      participationCategory: data.participationCategory,
    };

    const leadPayload = {
      leadPageName: "University Registration",
      leadOn: new Date().toISOString(),
      universityEmail: data.email,
      scheduleOn: new Date().toISOString(),
    };

    try {
      const universityRes = await fetch(saveOrUpdateUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(universityPayload),
      });

      if (!universityRes.ok) throw new Error("University save failed");

      const leadRes = await fetch(leadUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(leadPayload),
      });

      if (!leadRes.ok) throw new Error("Lead creation failed");

      alert("Registration successful!");
      reset();
    } catch (error) {
      console.error("Submission error:", error);
      alert("Submission failed. Check console for details.");
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="grid grid-cols-1 gap-3 max-w-md w-full"
    >
      {/* Display success or error messages */}
      {submitSuccess && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
          <strong className="font-bold">Success!</strong>
          <span className="block sm:inline">
            {" "}
            Registration completed successfully.
          </span>
        </div>
      )}

      {submitError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {submitError}</span>
        </div>
      )}

      <input
        type="text"
        placeholder="Institution Name"
        {...register("institutionName", { required: true })}
        className="border p-2 rounded"
      />
      {errors.institutionName && (
        <p className="text-red-500">Institution Name is required</p>
      )}

      <input
        type="text"
        placeholder="Website"
        {...register("website", { required: true })}
        className="border p-2 rounded"
      />
      {errors.website && <p className="text-red-500">Website is required</p>}

      <input
        type="text"
        placeholder="Point of Contact"
        {...register("contactPerson", { required: true })}
        className="border p-2 rounded"
      />
      {errors.contactPerson && (
        <p className="text-red-500">Point of contact is required</p>
      )}

      <input
        type="text"
        placeholder="Designation"
        {...register("designation", { required: true })}
        className="border p-2 rounded"
      />
      {errors.designation && (
        <p className="text-red-500">Designation is required</p>
      )}

      <input
        type="email"
        placeholder="Email"
        {...register("email", { required: true })}
        className="border p-2 rounded"
      />
      {errors.email && <p className="text-red-500">Email is required</p>}

      <input
        type="tel"
        placeholder="Mobile Number"
        {...register("mobileNumber", {
          required: true,
          pattern: /^[0-9]{10}$/,
        })}
        className="border p-2 rounded"
      />
      {errors.mobileNumber && (
        <p className="text-red-500">Enter valid 10-digit number</p>
      )}

      <input
        type="tel"
        placeholder="Alternate Mobile Number"
        {...register("alternateMobileNumber")}
        className="border p-2 rounded"
      />

      <select
        {...register("participationCategory", { required: true })}
        className="border p-2 rounded"
      >
        <option value="">Select your participation Category</option>
        <option value="Title Sponsor">Title Sponsor</option>
        <option value="Co Sponsor">Co Sponsor</option>
        <option value="Powered by Sponsor">Powered by Sponsor</option>
        <option value="Associate Sponsor">Associate Sponsor</option>
        <option value="Diamond">Diamond</option>
        <option value="Gold">Gold</option>
        <option value="Silver">Silver</option>
      </select>
      {errors.participationCategory && (
        <p className="text-red-500">Select a category</p>
      )}
      <div className="mt-5 w-full">
        <button
          type="submit"
          className="bg-[#45CD90] hover:bg-[#36b180] text-white py-2 rounded font-semibold mt-4 w-full"
        >
          Submit Now
        </button>
      </div>
    </form>
  );
};

export default UniversitiesRegistrationForm;
